import React, { isValidElement, cloneElement } from 'react';
import PropTypes from 'prop-types';
import { circle } from '@sie/web-common-utils';
import { classNamesFor } from '../utils/classNames';
import ProgressInfo from './ProgressInfo';
import Icon from '../Icon';
var cx;
/**
 * The CircularProgress component behaves similar to a HTML progress element and is
 * used to represent a progress state for an activity such as a trophy or UAM.
 *
 * - [Mobile Design System Progress](https://confluence.sie.sony.com/display/PPRC1/Progress+Bars)
 * - [MDN docs on progress](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/progress)
 *
 * ## Related Components
 * - [Progress](../Progress/README.md)
 *
 * ## Usage
 *
 * ```jsx
 * <CircularProgress
 *   size="s"
 *   value={50}
 *   max={100}
 *   unit="GB"
 * />
 * ```
 *
 * ## Props
 */

var CircularProgress = function CircularProgress(props) {
  var qa = props.qa,
      id = props.id,
      value = props.value,
      max = props.max,
      unit = props.unit,
      size = props.size,
      className = props.className,
      ariaLabel = props.ariaLabel,
      variant = props.variant,
      graphic = props.graphic,
      status = props.status;
  var percent = Math.min(value, max) / max;
  var isComplete = unit ? false : percent >= 1;

  var _CircularProgress$get = CircularProgress.getClassName({
    className: className,
    size: size,
    isComplete: isComplete
  }),
      root = _CircularProgress$get.root,
      chart = _CircularProgress$get.chart,
      baseCircle = _CircularProgress$get.baseCircle,
      progressCircle = _CircularProgress$get.progressCircle,
      info = _CircularProgress$get.info,
      graphicClass = _CircularProgress$get.graphic,
      statusClass = _CircularProgress$get.status;

  var viewWidth = 100;
  var viewHeight = viewWidth;
  var radius = viewWidth / 2;
  var perimeter = circle.perimeter(radius);
  var percentProgress = value / max;
  var gap = 0;
  var hasGraphic = graphic && /*#__PURE__*/isValidElement(graphic);

  if (variant === 'arc') {
    gap = 60;
  }

  var gapFactor = 1 - gap / 360;
  return /*#__PURE__*/React.createElement("span", {
    className: " psw-l-stack-center psw-l-inline"
  }, /*#__PURE__*/React.createElement("div", {
    "aria-valuemax": max,
    "aria-valuemin": "0",
    "aria-valuenow": value,
    role: "progressbar",
    "aria-label": ariaLabel,
    "data-qa": qa,
    id: id,
    className: root
  }, /*#__PURE__*/React.createElement("svg", {
    className: chart,
    viewBox: "-".concat(radius, " -").concat(radius, " ").concat(viewWidth, " ").concat(viewHeight),
    style: {
      transform: 'rotate(120deg)'
    },
    "data-qa": "".concat(qa, "#bar")
  }, /*#__PURE__*/React.createElement("circle", {
    className: baseCircle,
    cx: "0",
    cy: "0",
    r: "50%",
    strokeDasharray: perimeter * gapFactor,
    "data-qa": "".concat(qa, "#neededProgress")
  }), /*#__PURE__*/React.createElement("circle", {
    className: progressCircle,
    cx: "0",
    cy: "0",
    r: "50%",
    strokeDasharray: perimeter,
    strokeDashoffset: perimeter * (1 - percentProgress * gapFactor),
    "data-qa": "".concat(qa, "#currentProgress")
  })), /*#__PURE__*/React.createElement("span", {
    className: info
  }, !hasGraphic && /*#__PURE__*/React.createElement(ProgressInfo, {
    qa: qa,
    isComplete: isComplete,
    unit: unit,
    value: value,
    percent: percent,
    size: size
  }), hasGraphic && /*#__PURE__*/React.createElement("div", {
    className: graphicClass,
    "data-qa": "".concat(qa, "#graphic")
  }, graphic.type === Icon ? /*#__PURE__*/cloneElement(graphic, {
    size: 'auto'
  }) : /*#__PURE__*/cloneElement(graphic, {
    aspectRatio: '1:1'
  })))), status && /*#__PURE__*/React.createElement("span", {
    className: statusClass
  }, status));
};

CircularProgress.getClassName = function (_ref) {
  var className = _ref.className,
      size = _ref.size,
      isComplete = _ref.isComplete;
  return {
    root: cx('psw-circular-progress', "psw-circular-progress--".concat(size), {
      'psw-circular-progress--is-complete': isComplete
    }, 'psw-t-align-c', 'psw-l-stack', 'psw-l-anchor', className),
    chart: cx('psw-circular-progress-chart', {
      'psw-l-w-8 psw-l-h-8': size === 's'
    }, {
      'psw-l-w-11 psw-l-h-11': size === 'm'
    }, {
      'psw-l-w-12 psw-l-h-12': size === 'l'
    }),
    status: cx({
      'psw-t-size-4': size === 's'
    }, {
      'psw-t-size-5': size === 'm'
    }, {
      'psw-t-size-7': size === 'l'
    }),
    baseCircle: cx('psw-circular-progress-base'),
    progressCircle: cx('psw-circular-progress-bar'),
    info: cx('psw-center', 'psw-l-stack-center'),
    graphic: cx({
      'psw-l-w-6 psw-l-h-6': size === 's'
    }, {
      'psw-l-w-7 psw-l-h-7': size === 'm'
    }, {
      'psw-l-w-9 psw-l-h-9': size === 'l'
    })
  };
};

CircularProgress.useClassNames = function (x) {
  CircularProgress.classNames = x;
};

CircularProgress.defaultProps = {
  qa: '',
  id: '',
  unit: '',
  value: 0,
  max: 100,
  className: '',
  ariaLabel: null,
  size: 'm',
  variant: 'standard',
  graphic: null,
  status: ''
};
CircularProgress.propTypes = {
  /**
   * QA selector attribute used to target important elements for test validation
   */
  qa: PropTypes.string,

  /**
   * Presentational variants supported
   */
  variant: PropTypes.oneOf(['arc', 'standard']),

  /**
   * Allows adding a custom class or series of classes on the component
   * useful for setting margin or padding
   */
  className: PropTypes.string,

  /**
   * Aria label for CircularProgress
   */
  ariaLabel: PropTypes.string,

  /**
   * Adds the id to the root element of this component
   */
  id: PropTypes.string,

  /**
   * The value representing the progress (out of the max)
   */
  value: PropTypes.number,

  /**
   * The max value
   */
  max: PropTypes.number,

  /**
   * The associated unit
   */
  unit: PropTypes.string,

  /**
   * The size of the progress component
   */
  size: PropTypes.oneOf(['s', 'm', 'l']),

  /**
   * The graphic to display in the center of the arc variant
   */
  graphic: PropTypes.node,

  /**
   * Displays status value
   */
  status: PropTypes.string
};
cx = classNamesFor(CircularProgress);
export default CircularProgress;